<template>
  <div class="relative">
    <button
      type="button"
      class="btn btn-ghost btn-xs"
      aria-label="Edit text styles"
      :aria-pressed="hasOverride"
      :title="hasOverride ? 'Style override active' : 'Open style controls'"
      @click="open = !open">
      <span :class="hasOverride ? 'text-blue-600 font-semibold' : ''">Aa</span>
    </button>
    <div
      v-if="open"
      v-click-outside="close"
      class="absolute right-0 mt-1 z-20 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <div class="flex items-center justify-between mb-2">
        <span class="font-medium text-gray-900">Style</span>
        <button
          type="button"
          class="btn btn-ghost btn-xs"
          @click="reset">
          Reset
        </button>
      </div>

      <label v-if="kind !== 'image'" class="block mb-2">
        <span class="form-label">Text color</span>
        <div class="flex items-center gap-2 mt-1">
          <input
            type="color"
            :value="modelValue?.color || '#000000'"
            class="h-7 w-10 border border-gray-300 rounded"
            @input="patch('color', $event.target.value)">
          <input
            type="text"
            :value="modelValue?.color || ''"
            placeholder="#000000"
            class="form-input flex-1"
            @change="patch('color', $event.target.value)">
        </div>
        <div v-if="swatches.length" class="flex items-center gap-1 mt-1.5">
          <button
            v-for="hex in swatches"
            :key="`fg-${hex}`"
            type="button"
            class="w-5 h-5 rounded border border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            :style="{ backgroundColor: hex }"
            :title="`Brand color ${hex}`"
            @click="patch('color', hex)" />
          <span class="text-[10px] text-gray-400 ml-1">Brand</span>
        </div>
      </label>

      <label class="block mb-2">
        <span class="form-label">Background color</span>
        <div class="flex items-center gap-2 mt-1">
          <input
            type="color"
            :value="modelValue?.['background-color'] || '#ffffff'"
            class="h-7 w-10 border border-gray-300 rounded"
            @input="patch('background-color', $event.target.value)">
          <input
            type="text"
            :value="modelValue?.['background-color'] || ''"
            placeholder="#ffffff"
            class="form-input flex-1"
            @change="patch('background-color', $event.target.value)">
        </div>
        <div v-if="swatches.length" class="flex items-center gap-1 mt-1.5">
          <button
            v-for="hex in swatches"
            :key="`bg-${hex}`"
            type="button"
            class="w-5 h-5 rounded border border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            :style="{ backgroundColor: hex }"
            :title="`Brand color ${hex}`"
            @click="patch('background-color', hex)" />
          <span class="text-[10px] text-gray-400 ml-1">Brand</span>
        </div>
      </label>

      <label v-if="kind !== 'image'" class="block">
        <span class="form-label">Font size</span>
        <input
          type="text"
          :value="modelValue?.['font-size'] || ''"
          placeholder="e.g. 18px or clamp(16px, 2vw, 24px)"
          class="form-input w-full mt-1"
          @change="patch('font-size', $event.target.value)">
      </label>

      <p
        v-if="contrastWarning"
        class="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
        Contrast looks low. WCAG AA suggests ≥4.5:1 for body text.
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  kind: { type: String, default: 'text' }, // 'text' | 'image'
  swatches: { type: Array, default: () => []} // brand palette hex colors
})

const emit = defineEmits(['update:modelValue'])
const open = ref(false)

function close() { open.value = false }

function patch(prop, value) {
  const next = { ...(props.modelValue || {}) }
  const clean = (value || '').trim()
  if (clean === '') {
    delete next[prop]
  } else {
    next[prop] = clean
  }
  emit('update:modelValue', next)
}

function reset() {
  emit('update:modelValue', {})
}

const hasOverride = computed(() => {
  const m = props.modelValue || {}
  return Object.values(m).some((v) => v && String(v).trim() !== '')
})

// Cheap contrast estimate: only fires if BOTH color + background-color
// are hex values; converts to luminance and warns if ratio < 4.5.
const contrastWarning = computed(() => {
  const fg = props.modelValue?.color
  const bg = props.modelValue?.['background-color']
  if (!fg || !bg) return false
  const fgL = relLuminance(fg)
  const bgL = relLuminance(bg)
  if (fgL == null || bgL == null) return false
  const ratio = (Math.max(fgL, bgL) + 0.05) / (Math.min(fgL, bgL) + 0.05)
  return ratio < 4.5
})

function relLuminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  const r = ((n >> 16) & 0xff) / 255
  const g = ((n >> 8) & 0xff) / 255
  const b = (n & 0xff) / 255
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

const vClickOutside = {
  mounted(el, binding) {
    el._clickOutside = (ev) => { if (!el.contains(ev.target)) binding.value() }
    document.addEventListener('click', el._clickOutside, true)
  },
  unmounted(el) {
    document.removeEventListener('click', el._clickOutside, true)
  }
}
</script>
